import React, { useState } from "react";
import MarketingLayout from "@/components/layout/MarketingLayout";
import api from "@/lib/api";
import { Phone, Mail, MapPin, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Link } from "react-router-dom";

const faqs = [
  { q: "Where is DobhaiOfficial located?", a: "Our head office is in Islamabad, Pakistan, with a second studio in Lahore and an international desk covering Qatar and the UK." },
  { q: "Do you travel for weddings outside your home city?", a: "Yes — we shoot locally and travel abroad, including destination weddings, to capture your day wherever it takes place." },
  { q: "How far in advance should I book?", a: "We recommend reaching out 4–5 months ahead. For peak wedding season (December and January), book 6–7 months in advance to secure your date." },
  { q: "What's the best way to check availability?", a: "Fill out the booking form on our site, DM us on Instagram, or message us on WhatsApp — our team replies within a week." },
  { q: "Can packages be customized?", a: "Absolutely. Every package can be tailored to your event size and specific requirements, including extra hours or added services." },
  { q: "How do I confirm my date?", a: "Submit the booking form on our contact page and our team will walk you through the next steps." },
  { q: "When will I receive my photos and videos?", a: "Edited photo galleries are delivered within 20 days, videos within 3 months, and printed albums within 3 weeks of selecting your photos." },
  { q: "How are the final files delivered?", a: "Photos are delivered through our website and videos via Google Drive. Hard drive delivery is available if you provide one, or for an added fee." },
];

const offices = [
  {
    title: "Pakistan HQ",
    city: "Islamabad, Pakistan",
    address: "DobhaiOfficial Studio, Islamabad, Pakistan",
    phone: "+92 323 5464594",
    whatsapp: "https://wa.me/923235464594",
    email: "usmansajid25@gmail.com",
    testid: "office-pk",
  },
  {
    title: "International Desk",
    city: "Qatar & UK",
    address: "Doha, Qatar — London, UK",
    phone: "+974 5053 8523",
    whatsapp: "https://wa.me/97450538523",
    email: "usmansajid25@gmail.com",
    testid: "office-intl",
  },
];

export default function Contact() {
  const [region, setRegion] = useState("pakistan");
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", { ...form, region });
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      toast.success("Message sent — we'll reply within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingLayout>
      <section className="pt-20 pb-12 md:pt-28 container-x" data-testid="contact-hero">
        <div className="overline text-primary/70 mb-4">Get in Touch</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] max-w-3xl">
          Write to us. We read every word.
        </h1>
      </section>

      <section className="pb-20 container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="flex mb-6 border-b border-border">
              {["pakistan", "international"].map((r) => (
                <button
                  key={r}
                  onClick={() => setRegion(r)}
                  data-testid={`region-tab-${r}`}
                  className={`px-6 py-3 text-sm tracking-wide capitalize transition-colors border-b-2 -mb-px ${
                    region === r ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {r === "pakistan" ? "Pakistani Clients" : "International Clients"}
                </button>
              ))}
            </div>

            {submitted ? (
              <div className="border border-border bg-secondary/40 p-8 text-center" data-testid="contact-success">
                <Check size={28} strokeWidth={1.25} className="mx-auto text-primary" />
                <h3 className="font-serif text-2xl mt-4">Message received</h3>
                <p className="text-muted-foreground mt-2 text-sm">We typically reply within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} className="btn-primary mt-6">Send another</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-muted-foreground">Full Name</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full border border-input bg-background px-4 py-3 mt-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">Email</label>
                    <input
                      required type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full border border-input bg-background px-4 py-3 mt-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-muted-foreground">Phone</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full border border-input bg-background px-4 py-3 mt-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="overline text-muted-foreground">Subject</label>
                    <input
                      required
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full border border-input bg-background px-4 py-3 mt-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>
                <div>
                  <label className="overline text-muted-foreground">Message</label>
                  <textarea
                    required rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full border border-input bg-background px-4 py-3 mt-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary-filled disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>

          {/* Offices */}
          <aside className="lg:col-span-5 space-y-6">
            {offices.map((o) => (
              <div key={o.testid} className="border border-border bg-card p-7" data-testid={o.testid}>
                <div className="overline text-primary/70">{o.title}</div>
                <div className="font-serif text-2xl mt-2">{o.city}</div>
                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <MapPin size={14} strokeWidth={1.25} /> {o.address}
                  </div>
                  <a href={`tel:${o.phone}`} className="flex items-center gap-3 hover:text-primary">
                    <Phone size={14} strokeWidth={1.25} /> {o.phone}
                  </a>
                  <a href={`mailto:${o.email}`} className="flex items-center gap-3 hover:text-primary">
                    <Mail size={14} strokeWidth={1.25} /> {o.email}
                  </a>
                  <a href={o.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-primary">
                    <MessageCircle size={14} strokeWidth={1.25} /> WhatsApp us
                  </a>
                </div>
              </div>
            ))}
            <div className="aspect-[16/9] border border-border overflow-hidden">
              <iframe
                title="DobhaiOfficial Location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=74.338%2C31.519%2C74.368%2C31.549&amp;layer=mapnik"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </aside>
        </div>
      </section>
      
      <section className="pb-24 container-x" data-testid="contact-faq">
        <div className="max-w-3xl">
          <div className="overline text-primary/70 mb-4">FAQs</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-8">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="border-t border-border">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="font-serif text-base md:text-lg">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Added Section matched to the studio theme */}
      <section className="bg-card text-card-foreground py-24 border-t border-border" data-testid="still-have-question-section">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Still have a<br />question?
              </h2>
              <p className="text-sm text-muted-foreground font-mono max-w-md leading-relaxed">
                If you need clarity on anything at all, send us a message and we will guide you personally.
              </p>
              <div>
                <Link
                  to="/contact"
                  className="btn-primary-filled inline-flex items-center justify-center"
                >
                  Send Us A Message
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 border-t border-border lg:border-t-0 lg:border-l lg:border-border lg:pl-12 space-y-10">
              <div className="border-b border-border pb-8">
                <div className="overline text-primary/70 mb-2">Email</div>
                <div className="font-serif text-lg">usmansajid25@gmail.com</div>
              </div>

              <div className="border-b border-border pb-8">
                <div className="overline text-primary/70 mb-2">Response Time</div>
                <div className="font-serif text-lg">Within 24 hours, always</div>
              </div>

              <div>
                <div className="overline text-primary/70 mb-2">Consultations</div>
                <div className="font-serif text-lg">Video call, any time zone</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}